import { Toaster, toast } from 'sonner';
import { useLanguage } from './contexts/LanguageContext';
import { useTheme } from './contexts/ThemeContext';
import NavBar from './components/navigation/NavBar';
import Footer from './components/common/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import AccessibilityWidget from './components/common/AccessibilityWidget';
import FloatingChatSupport from './components/common/FloatingChatSupport';
import { Info, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

export const GlassyToast = ({ message, type = 'info', description }) => {
    const typeStyles = {
        success: 'bg-emerald-950/95 text-emerald-100 border-emerald-500/60 shadow-2xl shadow-emerald-950/50',
        error: 'bg-rose-950/95 text-rose-100 border-rose-500/70 shadow-2xl shadow-rose-950/60 ring-2 ring-rose-500/30',
        info: 'bg-blue-950/95 text-blue-100 border-blue-500/60 shadow-2xl shadow-blue-950/50',
        warning: 'bg-amber-950/95 text-amber-100 border-amber-500/60 shadow-2xl shadow-amber-950/50',
    };

    const icons = {
        success: <CheckCircle className="text-emerald-400 shrink-0" size={22} />,
        error: <XCircle className="text-rose-400 shrink-0 animate-bounce" size={22} />,
        info: <Info className="text-blue-400 shrink-0" size={22} />,
        warning: <AlertCircle className="text-amber-400 shrink-0" size={22} />,
    };

    return (
        <div className={`flex items-center gap-3.5 border-2 p-4 rounded-2xl shadow-2xl min-w-[320px] max-w-md backdrop-blur-2xl animate-in fade-in slide-in-from-bottom-4 duration-300 ${typeStyles[type] || typeStyles.info}`}>
            {icons[type]}
            <div className="flex-1 min-w-0">
                <p className="text-sm font-black uppercase tracking-tight text-white">{message}</p>
                {description && <p className="text-xs font-bold text-white/90 mt-0.5 leading-relaxed">{description}</p>}
            </div>
        </div>
    );
};

export const showToast = (message, type = 'info', description) => {
    toast.custom((t) => (
        <GlassyToast message={message} type={type} description={description} />
    ));
};

function AppContent({ children }) {
  const { dir } = useLanguage();
  const { theme } = useTheme();

  return (
    <div dir={dir} className="flex flex-col min-h-screen">
      <Toaster 
        theme={theme === 'dark' ? 'dark' : 'light'} 
        richColors 
        position="bottom-right" 
        toastOptions={{
            style: {
                borderRadius: '1rem',
                fontSize: '14px',
                fontWeight: '700',
            },
        }}
      />
      <NavBar />
      <AccessibilityWidget />
      <FloatingChatSupport />
      <ScrollToTop />
      <main className="pt-28 flex-grow flex flex-col">
        <div className={`flex-1 ${window.location.pathname.startsWith('/messages') ? '' : 'container mx-auto px-4 sm:px-6 lg:px-8 py-8'}`}>
          {children}
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default function Layout({ children }) {
  return (
    <AppContent>{children}</AppContent>
  );
}
